import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Sparkles, Users, Megaphone, Settings, PanelLeftClose, PanelLeftOpen, LogOut, CreditCard } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import useStore from '@/store/useStore';
import { useUiLanguage } from '@/lib/i18n';

const NAV_GROUPS = [
  {
    title: 'Overview',
    items: [
      { path: '/merchant/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/merchant/ai', label: 'AI Insights', icon: Sparkles },
    ],
  },
  {
    title: 'Loyalty',
    items: [
      { path: '/merchant/campaigns', label: 'Campaigns', icon: Megaphone },
      { path: '/merchant/customers', label: 'Customers', icon: Users },
      { path: '/merchant/redemptions', label: 'Redemptions', icon: CreditCard },
    ],
  },
  {
    title: 'Settings',
    items: [{ path: '/merchant/settings', label: 'Settings', icon: Settings }],
  },
];

export default function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, merchantUser, merchantLogout } = useStore();
  const { t } = useUiLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    merchantLogout();
    navigate('/merchant', { replace: true });
  };

  const isItemActive = (item) => {
    if (location.pathname === item.path) return true;
    if (location.pathname.startsWith(`${item.path}/`)) return true;
    return false;
  };

  return (
    <aside className={`sidebar-transition h-screen sticky top-0 border-r border-stone-200 bg-white flex flex-col ${sidebarCollapsed ? 'w-[74px]' : 'w-[250px]'}`} data-testid="merchant-sidebar">
      <div className="h-16 px-3 border-b border-stone-100 flex items-center justify-between">
        {!sidebarCollapsed ? (
          <div className="flex items-center gap-2">
            <img
              src={merchantUser?.logo_base64 || '/assets/flyback.png'}
              alt={merchantUser?.restaurant_name || t('Restaurant')}
              className="h-8 w-8 rounded-lg object-cover border border-stone-200"
            />
            <div className="min-w-0">
              <p className="text-sm font-bold tracking-tight text-brand truncate" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                {merchantUser?.restaurant_name || t('Restaurant')}
              </p>
              <p className="text-[10px] text-stone-400 truncate">{merchantUser?.name || t('Owner')}</p>
            </div>
          </div>
        ) : (
          <div />
        )}
        <button onClick={toggleSidebar} className="h-8 w-8 rounded-lg hover:bg-stone-100 flex items-center justify-center" data-testid="sidebar-toggle-btn">
          {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4 text-stone-500" /> : <PanelLeftClose className="w-4 h-4 text-stone-500" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV_GROUPS.map((group) => (
          <div key={group.title} className="space-y-1">
            {!sidebarCollapsed && <p className="px-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-stone-400">{t(group.title)}</p>}
            {group.items.map(({ path, label, icon: Icon }) => (
              <Link
                key={`${group.title}-${label}`}
                to={path}
                data-testid={`sidebar-${label.toLowerCase().replace(/\s/g, '-')}`}
                className={`h-10 rounded-xl px-3 flex items-center gap-3 text-sm font-medium transition-colors ${
                  isItemActive({ path })
                    ? 'bg-brand-soft text-brand border border-brand/20'
                    : 'text-stone-500 hover:bg-stone-50 hover:text-stone-700'
                }`}
                title={t(label)}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {!sidebarCollapsed && <span>{t(label)}</span>}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      <div className="px-3 pb-4 pt-3 border-t border-stone-100">
        <button onClick={() => setShowLogoutConfirm(true)} className="h-10 w-full rounded-xl px-3 flex items-center gap-3 text-sm text-stone-500 hover:bg-stone-50" data-testid="sidebar-logout-btn">
          <LogOut className="w-4 h-4 shrink-0" />
          {!sidebarCollapsed && <span>{t('Logout')}</span>}
        </button>
      </div>

      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{t('Log out of FlyBack?')}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{t('You can sign back in any time from this device.')}</DialogDescription>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setShowLogoutConfirm(false)} className="h-10 rounded-xl border border-stone-200 text-sm font-semibold hover:bg-stone-50">{t('Cancel')}</button>
            <button type="button" onClick={confirmLogout} className="h-10 rounded-xl gradient-brand-btn text-sm font-semibold" data-testid="merchant-confirm-logout-btn">{t('Log out')}</button>
          </div>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
